/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import OpportunityForm from "../../components/OpportunityForm";
import SideBar from "../../components/Sidebar";
import useWindowWidth from "../../utils/windowWidth";
import GuestLoginPrompt from "../../components/GuestLoginPrompt";
import { GET_USER_INFO } from "../../utils/constants";
import { makeAuthenticatedGet } from "../../utils/fetchUtils";
import { getGuestResumeStatus } from "../../utils/guestSession";

function ParsingForm() {
  const mainRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const replaceResume = (
    location.state as { replaceResume?: boolean } | null
  )?.replaceResume === true;
  const [hasSavedResume, setHasSavedResume] = useState(false);
  const [hasTemporaryGuestResume, setHasTemporaryGuestResume] = useState(false);
  const [guestResumeExpired, setGuestResumeExpired] = useState(false);
  const [isCheckingResume, setIsCheckingResume] = useState(true);


  useEffect(() => {
    document.title = 'Career Discovery - Nclude Portal';
    const token = localStorage.getItem("token");
    if (!token) {
      const guestResumeStatus = getGuestResumeStatus();
      if (guestResumeStatus === "valid" && !replaceResume) {
        navigate("/opportunity/chatbot", {
          replace: true,
          state: { useResumeContext: true },
        });
        return;
      }
      setHasSavedResume(guestResumeStatus === "valid");
      setHasTemporaryGuestResume(guestResumeStatus === "valid");
      setGuestResumeExpired(guestResumeStatus === "expired");
      setIsCheckingResume(false);
      return;
    }

    let isCurrent = true;
    const checkSavedResume = async () => {
      try {
        const response = await makeAuthenticatedGet(GET_USER_INFO);
        if (!isCurrent) return;
        const resumeAvailable = Boolean(response?.success && response?.user?.resume);
        if (resumeAvailable && !replaceResume) {
          localStorage.setItem("resume", "true");
          navigate("/opportunity/chatbot", {
            replace: true,
            state: { useResumeContext: true },
          });
          return;
        }
        setHasSavedResume(resumeAvailable);
        localStorage.setItem("resume", String(resumeAvailable));
      } catch (error) {
        console.error("Unable to check for a saved resume:", error);
      } finally {
        if (isCurrent) setIsCheckingResume(false);
      }
    };

    checkSavedResume();
    return () => {
      isCurrent = false;
    };
  }, [navigate, replaceResume]);

  useEffect(() => {
    // Set focus on the main content when the route changes
    if (!isCheckingResume && mainRef.current) {
      mainRef.current.focus();
    }
  }, [isCheckingResume, location]);

  //window size
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 769;

  return (
    <div className="containerr">
      {!isCheckingResume && (
        <GuestLoginPrompt serviceName="Career Discovery" promptKey="career-discovery" />
      )}
      {!isMobile && <Header />}
      <div className="page-container">
        <SideBar activePage="Opportunity"/>
        <main className="doc-container" id="main-content" tabIndex={-1}>
          <h1 className="display-title" ref={mainRef} tabIndex={-1}>{"Career discovery"}</h1>
          <p style={{ fontSize: "0.875rem", color: "#6c757d", marginTop: "0.25rem", marginBottom: "0.5rem" }} role="status" aria-live="polite">
            Agent status: Online
          </p>
          {!isCheckingResume && (
            <p className="accessibility-helper">
              {hasSavedResume
                ? "Upload a different resume below, or keep using the resume already available."
                : "For more personalized career guidance, you can upload a resume or continue without one."}
            </p>
          )}
          {isCheckingResume && <p className="resume-profile-status" role="status">Checking your profile for a saved resume…</p>}
          {!isCheckingResume && guestResumeExpired && (
            <p className="resume-profile-status resume-profile-status-warning" role="alert">
              Your temporary resume information has expired. Upload your resume again for personalized career guidance, or continue without a resume.
            </p>
          )}
          {!isCheckingResume && (
            <div className="component-container form-container">
              <OpportunityForm
                hasSavedResume={hasSavedResume}
                hasTemporaryGuestResume={hasTemporaryGuestResume}
                initiallyShowUpload={replaceResume}
              />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default ParsingForm;
